import { motion } from "framer-motion";
import { 
  sectionVariant, 
  staggerContainer, 
  itemVariant,
  viewportOnce,
  viewportOnceNear
} from "@/lib/motion";

export interface CapabilityContent {
  eyebrow: string;
  headline: string;
  description: string;
  footnote: string;
  phases: {
    number: string;
    title: string;
    question: string;
    insight: string;
    outcomes: string[];
  }[];
}

export const capabilityDefaults: CapabilityContent = {
  eyebrow: "How Growth Happens",
  headline: "Most companies optimize tactics. We optimize the sequence.",
  description: "Visibility without conversion burns budget. Conversion without retention caps growth. Scale without infrastructure breaks.",
  footnote: "Sometimes the constraint isn't marketing at all. We'll tell you if that's the case.",
  phases: [
    {
      number: "01", title: "Visibility", question: "Are the right people finding you?",
      insight: "Reach without intent burns budget. Most brands optimize for impressions when they should optimize for demand signals.",
      outcomes: ["Lower CPM", "Higher-quality traffic", "Market presence"],
    },
    {
      number: "02", title: "Performance", question: "Is attention converting to action?",
      insight: "The gap between click and customer is where most budgets die. Conversion is a clarity problem, not a UX problem.",
      outcomes: ["Higher conversion", "Improved ROAS", "Faster payback"],
    },
    {
      number: "03", title: "Scale", question: "Can momentum sustain itself?",
      insight: "Scaling without infrastructure breaks everything. You can't optimize what you can't measure.",
      outcomes: ["Sustainable economics", "Repeatable acquisition", "Compounding returns"],
    },
  ],
};

interface CapabilityProps {
  content?: CapabilityContent;
}

const Capability = ({ content: c = capabilityDefaults }: CapabilityProps) => {
  return (
    <section className="py-12 md:py-16 lg:py-20 border-t border-border">
      <div className="container-wide">
        <motion.div 
          className="max-w-2xl mb-8 md:mb-10 lg:mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={sectionVariant}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tight mb-5">
            {c.headline}
          </h2>
          <p className="text-muted-foreground leading-relaxed max-w-lg mb-3">
            {c.description}
          </p>
          <p className="text-xs text-muted-foreground/50 max-w-lg">
            {c.footnote}
          </p>
        </motion.div>

        <motion.div 
          className="grid md:grid-cols-3 gap-px bg-border"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnceNear}
          variants={staggerContainer}
        >
          {c.phases.map((phase) => (
            <motion.div
              key={phase.title}
              variants={itemVariant}
              className="group bg-background p-6 md:p-8 lg:p-12 transition-colors duration-150 hover:bg-muted/20"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-medium text-primary tracking-wider">{phase.number}</span>
                <div className="h-px flex-1 bg-border max-w-10" />
              </div>
              <h3 className="text-xl font-display font-medium text-foreground mb-2">{phase.title}</h3>
              <p className="text-base font-medium text-foreground/80 mb-4">{phase.question}</p>
              <p className="text-sm text-muted-foreground mb-8 leading-relaxed">{phase.insight}</p>
              <div className="pt-6 border-t border-border">
                <ul className="space-y-2.5">
                  {phase.outcomes.map((outcome) => (
                    <li key={outcome} className="flex items-center gap-3 text-sm text-foreground/70">
                      <div className="w-1 h-1 rounded-full bg-accent shrink-0" />
                      {outcome}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Capability;
